import { useSetRebuyItems } from "@babackapp/return-widget-components/hooks/use-set-rebuy-items";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import type {
  DraftLineItem,
  OrderLineItem,
} from "@babackapp/return-widget-components/types";
import type {
  CartItem,
  EmbedToWidgetMessage,
} from "@babackapp/return-widget-components/types/shop-now";
import {
  publicGetVariantPricing,
  type VariantPricingMarket,
} from "@babackapp/private-api-client/client/public-get-variant-pricing";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { postToParent } from "../lib/shop-now-embed";
import { useIsMerchant } from "./use-is-merchant";
import { usePlatform } from "./use-platform";
import { useWidgetParams } from "./use-widget-params";

type UseShopNowResolveResult = {
  items: CartItem[];
  currency: string;
  cartTotal: number;
  /** 1 + tax rate for HT shops. Apply to item.price * item.quantity to get TTC sub-total. */
  taxMultiplier: number;
  exchangeCreditLineItems: DraftLineItem[];
  orderLineItems: OrderLineItem[];
  /** True when the storefront handoff is unavailable and the in-widget catalogue picker is offered instead (merchant preview). */
  isCatalogPickerActive: boolean;
  handleAddMore: () => void;
  addItems: (items: CartItem[]) => void;
  handleConfirm: () => void;
  handleRemoveItem: (index: number) => void;
};

/**
 * Overlay current catalogue prices onto cart items fetched from /cart.js.
 *
 * BBS-193: /cart.js prices may be stale if the customer loaded the rebuy banner
 * before a price change. The catalogue (catalogProductVariantPricing) corrects
 * stale /cart.js prices in the display. The backend charges these prices once
 * their currency is confirmed to be the order's (see BBS-400 below), capped by
 * Shopify Admin live prices (draftOrderCalculate); otherwise it charges the
 * latter.
 *
 * BBS-358: the order's shipping country is passed so the backend prices in
 * the catalogue the order was actually bought in, checked against the order's
 * own lines — the catalogue alone only knows primary-market prices, and would
 * overwrite a correct /cart.js market price with them. Without a country
 * (store pickup…) the catalogue is used, as before.
 *
 * BBS-400: the catalogue only gives the list price. An app's automatic
 * discount (a Sales Genius sale…) exists only in the cart, so the cart's
 * discount rate is kept and applied to the catalogue's list price. The
 * repriced item is in the catalogue's currency — the order's when priced by
 * market, unknown otherwise, in which case the backend charges the catalogue.
 *
 * Variants without a price keep their original /cart.js price.
 */
async function refreshPrices(
  shopId: string,
  items: CartItem[],
  market: VariantPricingMarket,
): Promise<CartItem[]> {
  try {
    const variantIds = items.map((i) => String(i.variantId));
    const pricings = await publicGetVariantPricing(
      shopId,
      variantIds,
      market.countryCode ? market : undefined,
    );
    const priceMap = new Map(pricings.map((p) => [p.variantId, p.price]));
    const catalogueCurrency = market.countryCode
      ? (market.currency ?? undefined)
      : undefined;
    return items.map((item) => {
      const freshPrice = priceMap.get(String(item.variantId));
      if (freshPrice == null) return item;
      return {
        ...item,
        price: Math.round(freshPrice * getCartDiscountRatio(item)),
        originalPrice: freshPrice,
        currency: catalogueCurrency,
      };
    });
  } catch {
    // Network error or catalogue unavailable — fall back to /cart.js prices,
    // which the backend charges when the cart is in the order's currency.
    return items;
  }
}

/** Share of the list price the cart charges: 1 without a cart discount. */
function getCartDiscountRatio(item: CartItem): number {
  if (!item.originalPrice || item.originalPrice <= 0) return 1;
  return Math.min(Math.max(item.price / item.originalPrice, 0), 1);
}

export function useShopNowResolve(): UseShopNowResolveResult {
  const navigate = useNavigate();
  const platform = usePlatform();
  const isMerchant = useIsMerchant();
  const { orderId, shopInternalId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const setRebuyItems = useSetRebuyItems(orderId);
  const draft = useDraftReturn(orderId);

  const exchangeCreditLineItems = (draft?.lineItems ?? []).filter(
    (li) => li.resolution === "exchange-credit",
  );
  const orderLineItems: OrderLineItem[] = order?.lineItems ?? [];
  const marketCountryCode = order?.shippingAddress?.country_code;
  // Typed number by the generated client, a currency code at runtime.
  const marketCurrency = order?.currency?.customerCurrency
    ? String(order.currency.customerCurrency)
    : null;
  // Prices come from the customer's market catalog, so they display in its currency.
  const displayCurrency =
    marketCurrency ?? order?.currency?.shopCurrency ?? "EUR";
  const market = useMemo<VariantPricingMarket>(
    () => ({ countryCode: marketCountryCode, currency: marketCurrency }),
    [marketCountryCode, marketCurrency],
  );

  // Items live directly in the draft — no intermediate sessionStorage buffer.
  const items = draft?.rebuyItems ?? [];

  // Generation counter: incremented on every refresh start to discard superseded results.
  const refreshGen = useRef(0);

  useEffect(() => {
    postToParent({ type: "WIDGET_SHOP_NOW_READY" });
  }, []);

  // Prices are refreshed against the order's market, so not before the order
  // has loaded. WIDGET_SHOP_NOW_READY goes out on mount and SHOP_NOW_RESUME can
  // come back first: those items are kept as sent, and the order's arrival
  // refreshes the current cart once (also covers a page refresh after
  // shopping).
  const isOrderLoaded = !!order;
  const didRefreshOnOrderLoad = useRef(false);
  useEffect(() => {
    if (didRefreshOnOrderLoad.current || !isOrderLoaded) return;
    didRefreshOnOrderLoad.current = true;
    if (!items.length) return;
    const gen = ++refreshGen.current;
    refreshPrices(shopInternalId, items, market).then((refreshed) => {
      if (refreshGen.current !== gen) return;
      setRebuyItems(refreshed);
    });
  }, [isOrderLoaded, market]);

  const applyItems = useCallback(
    async (raw: CartItem[]) => {
      const gen = ++refreshGen.current;
      if (!isOrderLoaded) {
        setRebuyItems(raw);
        return;
      }
      const refreshed = await refreshPrices(shopInternalId, raw, market);
      if (refreshGen.current !== gen) return;
      setRebuyItems(refreshed);
    },
    [shopInternalId, setRebuyItems, market, isOrderLoaded],
  );

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const msg = event.data as EmbedToWidgetMessage;
      if (msg?.type === "SHOP_NOW_RESUME") {
        void applyItems(msg.items);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [applyItems]);

  const cartTotal = items.reduce(
    (sum, item) =>
      sum + item.price * item.quantity * financialSummary.taxMultiplier,
    0,
  );

  const isCatalogPickerActive = platform === "baback-office" && isMerchant;

  const handleAddMore = () =>
    postToParent({ type: "SHOP_NOW_START", clearCart: false });

  const addItems = (added: CartItem[]) => {
    if (added.length === 0) return;
    void applyItems([...items, ...added]);
  };

  const handleRemoveItem = (index: number) =>
    setRebuyItems(items.filter((_, i) => i !== index));

  const handleConfirm = () => {
    // items already committed to draft.rebuyItems — just navigate
    const remaining = financialSummary.exchangeCreditTotal - cartTotal;
    navigate(remaining > 0 ? "../shop-now-remaining-credit" : "../return-mode");
  };

  return {
    items,
    currency: displayCurrency,
    cartTotal,
    taxMultiplier: financialSummary.taxMultiplier,
    exchangeCreditLineItems,
    orderLineItems,
    isCatalogPickerActive,
    handleAddMore,
    addItems,
    handleConfirm,
    handleRemoveItem,
  };
}
